import { useState } from 'react';

const AddIndicator = ({ token, ticker }) => {
	const defaultValues = {
		indicator: '',
		color: '#2563eb',
		multiplier: 1,
		movingAverageType: 'sma',
		window: 14,
		fast: 12,
		slow: 26,
		signal: 9,
		rsiOversold: 30,
		rsiOverbought: 70,
	};
	const [formData, setFormData] = useState(defaultValues);

	const handleChange = (event) => {
		const { name, value } = event.target;
		if (name === 'indicator') {
			switch (value) {
				case 'atr':
				case 'rsi':
					setFormData({
						...formData,
						window: 14,
					});
					break;
				case 'ema':
				case 'sma':
					setFormData({
						...formData,
						window: 10,
					});
					break;
				case 'bb':
					setFormData({
						...formData,
						window: 20,
					});
					break;
			}
		}
		setFormData((prevState) => ({
			...prevState,
			[name]: value,
		}));
	};

	const addIndicator = () => {
		window.HSOverlay.close('#hs-focus-indicator-modal');
		setFormData(defaultValues);
		document.getElementById('indicator').selectedIndex = 0;
		document.dispatchEvent(new CustomEvent('AddIndicator', { detail: formData }));
	};

	return (
		<div className="flex mt-2 ml-3 md:justify-start justify-end">
			<button
				type="button"
				className="inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:pointer-events-none dark:text-blue-500 dark:hover:text-blue-400 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
				data-hs-overlay="#hs-focus-indicator-modal"
			>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4">
					<path
						fillRule="evenodd"
						d="M9.808 4.057a.75.75 0 0 1 .92-.527l3.116.849a.75.75 0 0 1 .528.915l-.823 3.121a.75.75 0 0 1-1.45-.382l.337-1.281a23.484 23.484 0 0 0-3.609 3.056.75.75 0 0 1-1.07.01L6 8.06l-3.72 3.72a.75.75 0 1 1-1.06-1.061l4.25-4.25a.75.75 0 0 1 1.06 0l1.756 1.755a25.015 25.015 0 0 1 3.508-2.85l-1.46-.398a.75.75 0 0 1-.526-.92Z"
						clipRule="evenodd"
					/>
				</svg>
				Add Indicator
			</button>

			<div
				id="hs-focus-indicator-modal"
				className="hs-overlay hidden w-full h-full fixed top-0 start-0 z-[80] overflow-x-hidden overflow-y-auto pointer-events-none"
			>
				<div className="hs-overlay-open:mt-7 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-0 opacity-0 ease-out transition-all sm:max-w-lg sm:w-full m-3 sm:mx-auto">
					<div className="flex flex-col bg-white border shadow-sm rounded-xl pointer-events-auto dark:bg-gray-800 dark:border-gray-700 dark:shadow-slate-700/[.7]">
						<div className="flex justify-between items-center py-3 px-4 border-b dark:border-gray-700">
							<h3 className="font-bold text-gray-800 dark:text-white">Add Indicator</h3>
							<button
								type="button"
								className="flex justify-center items-center w-7 h-7 text-sm font-semibold rounded-full border border-transparent text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none dark:text-white dark:hover:bg-gray-700 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
								data-hs-overlay="#hs-focus-indicator-modal"
							>
								<span className="sr-only">Close</span>
								<svg
									className="flex-shrink-0 w-4 h-4"
									xmlns="http://www.w3.org/2000/svg"
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M18 6 6 18" />
									<path d="m6 6 12 12" />
								</svg>
							</button>
						</div>
						<div className="p-4 overflow-y-auto">
							<label htmlFor="indicator" className="block text-sm font-medium mb-2 dark:text-white">
								Indicator
							</label>
							<select
								defaultValue={formData.indicator || ''}
								name="indicator"
								id="indicator"
								onChange={handleChange}
								className="py-3 px-4 pe-9 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400 dark:focus:ring-gray-600"
							>
								<option>Select Indicator</option>
								<option value="atr">Average True Range (ATR)</option>
								<option value="bb">Bollinger Band</option>
								<option value="ema">Exponential Moving Average (EMA)</option>
								<option value="sma">Simple Moving Average (SMA)</option>
								<option value="macd">MACD</option>
								<option value="rsi">Relative Strength Index (RSI)</option>
							</select>
						</div>
						{formData.indicator !== '' && ['sma', 'atr', 'ema'].includes(formData.indicator) && (
							<div className="p-4 overflow-y-auto">
								<label htmlFor="indicator" className="block text-sm font-medium mb-2 dark:text-white">
									Window Size
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="window"
												onChange={handleChange}
												value={formData.window || 14}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.window <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														window: --formData.window,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														window: ++formData.window,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>
								<div className="w-full flex justify-between items-center gap-x-1 mt-3">
									<label htmlFor="hs-color-input" className="block text-sm font-medium mb-2 dark:text-white">
										Choose Graph Color
									</label>
									<input
										type="color"
										onChange={handleChange}
										name="color"
										className="p-1 h-10 w-14 block bg-white border border-gray-200 cursor-pointer rounded-lg disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:border-gray-700"
										id="hs-color-input"
										value={formData.color}
										title="Choose your color"
									/>
								</div>
							</div>
						)}

						{formData.indicator !== '' && ['rsi'].includes(formData.indicator) && (
							<div className="p-4 overflow-y-auto">
								<label htmlFor="indicator" className="block text-sm font-medium mb-2 dark:text-white">
									Window Size
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="window"
												onChange={handleChange}
												value={formData.window || 14}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.window <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														window: --formData.window,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														window: ++formData.window,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>

								<label htmlFor="rsiOverbought" className="block text-sm font-medium mb-2 dark:text-white">
									Overbought Threshold
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="rsiOverbought"
												id="rsiOverbought"
												onChange={handleChange}
												value={formData.rsiOverbought || 12}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.rsiOverbought <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														rsiOverbought: --formData.rsiOverbought,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														rsiOverbought: ++formData.rsiOverbought,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>

								<label htmlFor="rsiOversold" className="block text-sm font-medium mb-2 dark:text-white">
									Oversold Threshold
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="rsiOversold"
												id="rsiOversold"
												onChange={handleChange}
												value={formData.rsiOversold || 12}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.rsiOversold <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														rsiOversold: --formData.rsiOversold,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														rsiOversold: ++formData.rsiOversold,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>
							</div>
						)}

						{formData.indicator !== '' && ['macd'].includes(formData.indicator) && (
							<div className="p-4 overflow-y-auto">
								<label htmlFor="fast" className="block text-sm font-medium mb-2 dark:text-white">
									Fast
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="fast"
												id="fast"
												onChange={handleChange}
												value={formData.fast || 12}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.fast <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														fast: --formData.fast,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														fast: ++formData.fast,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>

								<label htmlFor="slow" className="mt-2 block text-sm font-medium mb-2 dark:text-white">
									Slow
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="slow"
												id="slow"
												onChange={handleChange}
												value={formData.slow || 12}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.slow <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														slow: --formData.slow,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														slow: ++formData.slow,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>

								<label htmlFor="signal" className="mt-2 block text-sm font-medium mb-2 dark:text-white">
									Signal
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="signal"
												id="signal"
												onChange={handleChange}
												value={formData.signal || 9}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.signal <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														signal: --formData.signal,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														signal: ++formData.signal,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>
							</div>
						)}

						{formData.indicator !== '' && ['bb'].includes(formData.indicator) && (
							<div className="p-4 overflow-y-auto">
								<label htmlFor="fast" className="block text-sm font-medium mb-2 dark:text-white">
									Window Size
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="window"
												id="window"
												onChange={handleChange}
												value={formData.window || 26}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.window <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														window: --formData.window,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														window: ++formData.window,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>

								<label htmlFor="slow" className="mt-2 block text-sm font-medium mb-2 dark:text-white">
									Multiplier
								</label>
								<div className="bg-white border border-gray-200 rounded-lg dark:bg-slate-700 dark:border-gray-700">
									<div className="w-full flex justify-between items-center gap-x-1">
										<div className="grow py-2 px-3">
											<input
												className="w-full p-0 bg-transparent border-0 text-gray-800 focus:ring-0 dark:text-white"
												type="text"
												name="multiplier"
												id="multiplier"
												onChange={handleChange}
												value={formData.multiplier || 1}
												data-hs-input-number-input
											/>
										</div>
										<div className="flex items-center -gap-y-px divide-x divide-gray-200 border-s border-gray-200 dark:divide-gray-700 dark:border-gray-700">
											<button
												type="button"
												disabled={formData.slow <= 1}
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														multiplier: --formData.multiplier,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
												</svg>
											</button>
											<button
												type="button"
												onClick={() =>
													setFormData((prevState) => ({
														...prevState,
														multiplier: ++formData.multiplier,
													}))
												}
												className="w-10 h-10 inline-flex justify-center items-center gap-x-2 text-sm font-medium last:rounded-e-lg bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
											>
												<svg
													className="flex-shrink-0 w-3.5 h-3.5"
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M5 12h14" />
													<path d="M12 5v14" />
												</svg>
											</button>
										</div>
									</div>
								</div>

								<div className="p-1 overflow-y-auto">
									<label htmlFor="signal" className="mt-2 block text-sm font-medium mb-2 dark:text-white">
										Type
									</label>
									<select
										defaultValue={formData.movingAverageType || 'sma'}
										name="movingAverageType"
										id="movingAverageType"
										onChange={handleChange}
										className="py-3 px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400 dark:focus:ring-gray-600"
									>
										<option value="sma">Simple Moving Average (SMA)</option>
										<option value="ema">Exponential Moving Average (EMA)</option>
									</select>
								</div>
							</div>
						)}

						<div className="flex justify-end items-center gap-x-2 py-3 px-4 border-t dark:border-gray-700">
							<button
								type="button"
								className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-slate-900 dark:border-gray-700 dark:text-white dark:hover:bg-gray-800 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
								data-hs-overlay="#hs-focus-indicator-modal"
							>
								Close
							</button>
							<button
								type="button"
								onClick={addIndicator}
								className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
							>
								Apply Indicator
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AddIndicator;
